<template>
  <Loader
    :key="data.id"
    :value="promises.fetchOne"
    class="residential-mutate view"
    :data-property-type="((item.properties || []).find(property => property.key === 'propertyType') || {}).value"
    :message="$t('loading', { resource: this.labels.residential.toLowerCase() }) + '...'"
  >
    <div v-if="data.id" class="container">
      <HeaderComponent :title="$metaInfo.title" :breadcrumb="breadcrumb">
        <template #center>
          <transition name="fade">
            <DraftAlert v-if="hasActiveDraft" />
          </transition>
        </template>
        <template #right>
          <div class="row wrap g-1">
            <Preview :item="item"></Preview>

            <Actions
              v-if="item.id"
              placement="residential"
              :query="{ residential: id, 'kvass-draft': draft || showDraft }"
            />

            <ButtonComponent
              v-if="!showDraft"
              :label="saveState.save.label"
              :success-label="$t('saved')"
              :loading-label="$t('saving')"
              :icon="['fal', saveState.save.icon]"
              :theme="saveState.save.theme"
              :promise="promises.mutate"
              :class="{
                'elder-button--disabled': saveState.save.disabled,
                'elder-button--invalid': saveState.save.invalid,
              }"
              v-tooltip="{ content: saveState.save.tooltip }"
              @click="submit"
            />

            <Draft
              v-else
              v-model="draft"
              :saveState="saveState"
              :reference="{ ref: reference, onModel: 'Project' }"
              :submit="submit"
              @reset="() => fetch({ id: id }, { draft: draft })"
              @input="
                draftId => {
                  item.draftId = draftId
                  key += 1
                }
              "
            />
          </div>
        </template>
      </HeaderComponent>

      <StepperComponent
        :key="key"
        :value="steps"
        ref="stepper"
        :stateKey="stateKey"
        state-handler="route"
        @submit="submit"
      >
        <template #before>
          <div class="residential-mutate__template-info">
            <Badge v-if="item.template" theme="secondary" style="cursor: default">
              <!-- <HelperPopover template="" /> -->
              <span> {{ `${$t('from')} ${$tc('unitCatalog', 1).toLowerCase()}: ` }}</span>

              <router-link
                :to="{ name: 'residential-template-edit', params: { id: item.template.id, step: 'presentation' } }"
              >
                {{ item.template.internalName }}
              </router-link>
            </Badge>
          </div>
        </template>
        <template #after="{ meta, next, prev }">
          <div class="row center g-1">
            <ButtonComponent
              v-if="meta.prev"
              :label="$t('prev')"
              type="submit"
              :icon="['fal', 'angle-left']"
              icon-placement="left"
              @click="onNavigation(prev)"
            />
            <ButtonComponent
              v-if="meta.next"
              :label="$t('next')"
              type="submit"
              :icon="['fal', 'angle-right']"
              @click="onNavigation(next)"
            />
            <ButtonComponent
              v-if="!meta.next"
              :label="$t('complete')"
              :success-label="$t('completed')"
              :loading-label="$t('saving')"
              :icon="['fal', 'check']"
              theme="primary"
              type="button"
              :disabled="!validator.passes"
              :promise="promises.mutate"
              @click="complete"
            />
          </div>
        </template>
      </StepperComponent>
    </div>

    <FirstUnitCreatedAlert :show.sync="hasCreatedFirstUnit" :value="item" />

    <ModalComponent :show="showSoldDialog" @close="showSoldDialog = false" clickaway>
      <Card variant="prompt" style="max-width: 400px">
        <template #default>
          <div class="text--center">
            <FontAwesomeIcon :icon="['fal', 'info-circle']" class="fade" size="2x" />
            <p>{{ $t('onSoldStatusDialog') }}</p>
            <p>{{ $t('bidUploadInfo') }}</p>
          </div>
        </template>
        <template #actions>
          <ButtonComponent :label="$t('cancel')" :icon="['fal', 'times']" @click="showSoldDialog = false" />

          <ButtonComponent
            :label="$t('next')"
            theme="primary"
            :icon="['fal', 'arrow-to-right']"
            tag="router-link"
            style="margin-left: auto"
            :to="{ name: 'residential-bid-create', params: { id: data.id } }"
          />
        </template>
      </Card>
    </ModalComponent>
  </Loader>
</template>

<script>
import Model from '@/models/Residential/base'
import ModelMixin from '@/mixins/Model'
import ValidatorMixin from '@/mixins/validator-v2'

import { mapState, mapActions, mapGetters } from 'vuex'
import { getValidatorConfig } from '@kvass/custom-fields'
import { i18n, mapi18n } from '@/i18n'
import Bus from '@/bus'
import API from '@/api/Residential'

import Steps from './Steps'
import { StepperComponent } from 'vue-elder-stepper'
import { DropdownComponent } from 'vue-elder-dropdown'
import Card from '@/components/Card-v2'
import { ModalComponent } from 'vue-elder-modal'
import HeaderComponent from '@/components/Header'
import FirstUnitCreatedAlert from '@/components/Residential/FirstUnitCreatedAlert'
import defaultFields from '@/components/Residential/StepsFields/defaults.js'
import fields from '@/components/Residential/StepsFields/fields.js'
import { getFieldsValues, setFieldsValue } from '@/components/Residential/StepsFields/utils.js'
import Badge from '@/components/Badge'
import HelperPopover from '@/components/HelperPopover/Component'
import DraftAlert from '@/components/Draft/Alert'
import Preview from '@/components/Preview'
import Draft from '@/components/Draft/Component'

import Actions from '@/components/Actions'
import {
  PartialUpdate,
  buildStepsFields,
  setByPath,
  mergeAllDeepRight,
  ObjectToDotNotation,
  getButtonThemes,
} from '@/utils'
import { mergeDeepRight } from 'ramda'
import { getExtensions } from '@/components/Tiptap/Extensions'

let tempChanges = {}

function getStatusSuffix(status, addons) {
  if (status === 'sold' && ['project:bid', 'project:shop-purchase'].some(addon => addons.includes(addon))) return
  if (addons.includes('project:presentation')) return
  if (!['draft', 'upcoming'].includes(status)) return `(${i18n.t('requiresUpgrade').toLowerCase()})`
}
const soldStates = ['sold', 'rented']
export { getStatusSuffix }

export default {
  mixins: [
    ValidatorMixin({
      rules: 'rules',
      labels: 'labels',
      data: 'internalData',
    }),
    ModelMixin({
      source: 'item',
      model: Model,
      guard: true,
      transform: {
        number: v => v && parseInt(v),
        'basis-basis': v => v,
        properties: arr =>
          arr.map((a, index) => {
            if (a.value) a.value = a.value.toString()
            a.order = index + 1
            return a
          }),
      },
      preTransform: {
        owners: v => (v || []).map(e => (e && e.id) || e),
        reservedBy: v => (v || []).map(e => (e && e.id) || e),
      },
    }),
  ],
  props: {
    id: String,
  },
  provide() {
    return {
      RichTextOptions: {
        commands: getExtensions(),
        button: {
          internalItems: `/api/resource/post?field.label=title&field.value=id&field.thumbnail=media.cover&references=${this.id}`,
          anchorItems: [
            { value: '#brochure', label: this.$t('brochure') },
            { value: '#lead', label: this.$t('leadForm') },
            { value: '#flatfinder', label: this.$tc('unitSelector', 1) },
          ],
          getThemes: () =>
            getButtonThemes(this.item, 'project', {
              source: ['project'],
            }),
          colors: ['primary', 'secondary', 'custom'],
          enableIcon: true,
        },
      },
    }
  },
  data() {
    return {
      stateKey: 'step',
      ready: false,
      displayNameState: false,
      internalData: {},
      saleTypeWatcher: null,
      statusWatcher: null,
      view: 'residential',
      showSoldDialog: false,
      hasCreatedFirstUnit: false,
      draft: true,
      key: 0,
    }
  },
  watch: {
    '$route.params.step': {
      handler(value, oldVal) {
        if (oldVal === 'presentation' && value === 'shop' && this.$path('project.stats.count', this.item) === 1) {
          const lsItem = `first-unit-is-created-success-message-displayed:${this.item.project.id}`
          if (localStorage.getItem(lsItem) === 'true') return
          this.hasCreatedFirstUnit = true
          localStorage.setItem(lsItem, 'true')
        }
      },
    },
    id: {
      handler(id) {
        if (this.saleTypeWatcher) this.saleTypeWatcher()
        if (this.statusWatcher) this.statusWatcher()
        return this.fetch({ id }, { draft: this.draft }).then(res => {
          this.ready = true

          this.internalData = this.valueComp

          tempChanges = {
            initialState: this.data.sale.type,
          }
          this.saleTypeWatcher = this.$watch('data.sale.type', this.onSaleTypeChange)
          this.statusWatcher = this.$watch('data.status', this.statusUpdate)
        })
      },
      immediate: true,
    },
  },
  computed: {
    ...mapState('Residential', ['item', 'promises', 'states']),
    ...mapGetters('Residential', ['name', 'getDisabledField']),

    reference() {
      return this.$path('item.project.id')
    },

    hasActiveDraft() {
      return typeof this.draft === 'string'
    },
    showDraft() {
      return this.$path('item.project.metadata.draftAvailable') || this.hasActiveDraft
    },
    units() {
      return this.$path('project.metadata.limits.units', this.item)
    },
    bid() {
      return this.$path('item.contract.bid')
    },
    addons() {
      return this.$path('project.metadata.addons', this.item) || []
    },
    pattern(state) {
      return this.$path('item.project.metadata.residentialNumberPattern', state.item)
    },
    ownerReservedState() {
      const status = this.data.status
      switch (status) {
        case 'reserved':
          return {
            label: this.$t('reservedBy'),
            key: 'reservedBy',
          }
        case 'sold':
        case 'rented':
          return {
            label: this.$tc('owner', 2),
            key: 'owners',
            disabled: Boolean(this.getDisabledField('owners') || !soldStates.includes(status)),
            tooltip: {
              content: !soldStates.includes(status)
                ? this.$t('unitMustBeSoldToSetOwner')
                : this.getDisabledField('owners'),
            },
          }
      }
    },
    labels() {
      return {
        ...mapi18n([{ properties: 'residentialProperties' }, 'residential']),
        ...this.customFieldValidatorConfig.labels,
      }
    },
    rules() {
      return {
        ...this.customFieldValidatorConfig.rules,
      }
    },
    saveState() {
      let base = {
        save: {
          label: this.showDraft ? this.$t('saveDraft') : this.$t('save'),
          icon: 'arrow-to-bottom',
          disabled: !this.validator.passes || !this.hasChanges,
          theme: this.showDraft ? 'secondary' : 'primary',
        },
        publish: {
          label: this.$t('publish'),
          icon: 'chevron-down',
          disabled: !this.validator.passes || (!this.hasChanges && !this.draft),
          theme: 'secondary',
        },
      }

      if (!this.hasChanges) {
        base.save.tooltip = this.$t('noUnsavedChanges')

        if (!this.draft) {
          base.publish.tooltip = this.$t('noUnsavedChanges')
        }
      }

      if (!this.states.fetchOne && !this.validator.passes) {
        const invalid = {
          invalid: true,
          icon: 'exclamation-circle',
          tooltip: Object.values(this.validator.errors.all()).join('<br>'),
        }

        Object.keys(base).forEach(key => {
          base[key] = { ...base[key], ...invalid }
        })
      }

      return base
    },
    valueComp: {
      get() {
        //map data to fields
        return {
          ...getFieldsValues(this.customFieldSchemas, {
            ...this.data,
            showDisplayName: this.showDisplayName,
          }),
          ...this.data,
          ...(this.$path('customFields', this.data) || {}),
          view: this.view,
          ownerReservedState: this.ownerReservedState,
          DisabledFeatures: this.$store.state.enums.DisabledFeatures,
        }
      },
      set(newVal) {
        //if slot -> update single field
        if (newVal.slot) {
          const { key, value } = newVal
          return setByPath(key, value, this.data)
        }

        const fieldsData = setFieldsValue(newVal, this.customFieldSchemas)

        //not slot -> customFields object is newVal -> Tranform this to fit merge with data
        this.data = mergeAllDeepRight(this.data, fieldsData)
        this.showDisplayName = fieldsData.showDisplayName

        this.internalData = newVal
      },
    },

    disabledFields() {
      const keys = Object.keys(ObjectToDotNotation(this.valueComp))
      const items = this.$path('metadata.disabledFields', this.item)
      let result = Object.fromEntries(
        Object.entries(items).map(([key, value]) => {
          const newKey = keys.find(i => {
            return i.includes(key.replaceAll('.', '-')) || i.endsWith(key.split('.').pop())
          })

          return [newKey, value]
        }),
      )

      //disable checkbox if displayName is disabled
      if (items.hasOwnProperty('displayName')) {
        result['basis-residential.showDisplayName'] = items.displayName
      }
      return result
    },

    steps() {
      let props = {
        value: this.valueComp,
        disabledFields: this.disabledFields,
        labels: this.labels,
        validator: this.validator,
        promises: this.promises,
        customFieldSchemas: this.customFieldSchemas,
        fields: buildStepsFields(Array.from(fields.values()), this.customFieldSchemas, this),
      }

      let listeners = {
        submit: () => this.submit(),
        updateValue: e => (this.valueComp = e),
      }

      return Steps()
        .filter(e => {
          switch (e.id) {
            case 'shop':
              return (
                this.$store.getters['Project/hasRole'](/^shop/, this.item.project) &&
                !this.$store.state.enums.DisabledFeatures.includes('project:shop')
              )
            case 'residential-account':
              return (
                this.$store.getters['Project/hasRole'](/manager$/, this.item.project) &&
                !this.$store.state.enums.DisabledFeatures.includes('project:residential-account')
              )
            default:
              return true
          }
        })
        .map(raw => {
          const { icon, ...s } = raw
          s.props = {
            ...(s.props || {}),
            ...props,
          }
          s.listeners = {
            ...(s.listeners || {}),
            ...listeners,
          }

          return s
        })
    },
    breadcrumb() {
      return [
        { title: this.$tc('project', 2), action: { name: 'projects' } },
        {
          title: this.$path('item.project.name') || `${this.$tc('loading')}...`,
          action: {
            name: 'project-dashboard',
            params: { id: this.$path('item.project.id') },
          },
        },
        {
          title: this.$tc('unitList'),
          action: {
            name: 'project-edit',
            params: { id: this.$path('item.project.id'), step: 'residentials' },
          },
        },

        {
          component: 'Dropdown',
          props: {
            current: { label: this.$metaInfo.title, value: this.item.id },
            items: () => {
              return API.get(
                {
                  filter: {
                    project: this.$path('item.project.id'),
                  },
                },
                {
                  query: 'Residentials',
                  draft: this.draft,
                },
              ).then(res =>
                res.map(i => ({
                  label: i.name,
                  value: i.id,
                  tag: 'div',
                  action: () => {
                    let answer = true
                    if (this.hasChanges) {
                      answer = window.confirm(this.$t('unsavedChangesDialog'))
                    }

                    if (answer) {
                      this.displayNameState = false

                      this.$router.push({
                        name: 'residential-edit',
                        params: { id: i.id, step: this.$route.params.step || 'basis' },
                      })
                    }
                  },
                })),
              )
            },
          },
        },
      ]
    },

    customFieldValidatorConfig() {
      if (!this.customFieldSchemas.length || !this.data) return {}
      return getValidatorConfig([...this.customFieldSchemas], this.data, { prefix: '' })
    },
    customFieldSchemas() {
      return [
        ...[...Array.from(defaultFields.values() || []).map(i => i(this))].filter(i => {
          const placement = i.placement instanceof Array ? i.placement : [i.placement]
          return placement.map(p => p.split(':').shift()).includes('residential')
        }),
        ...[
          ...(this.$path('data.customFieldsConfig') || []),
          ...(this.$path('item.project.customFieldsConfig') || [])
            .filter(e => (this.$path('placement', e) || '').startsWith('residential:'))
            .map(e => ({ ...e, placement: e.placement.replace('residential:', '') })),
        ]
          .map(c =>
            c.schema.map(e => ({
              ...e,
              placement: c.placement || e.placement,
              dataKey: 'customFields',
              isCustom: true,
            })),
          )
          .flat(),
      ]
    },

    ownerReservedState() {
      const status = this.data.status
      switch (status) {
        case 'reserved':
          return {
            key: 'reservedBy',
          }
        case 'sold':
        case 'rented':
          return {
            key: 'owners',
            disabled: Boolean(this.getDisabledField('owners') || !soldStates.includes(status)),
            tooltip: {
              content: !soldStates.includes(status)
                ? this.$t('unitMustBeSoldToSetOwner')
                : this.getDisabledField('owners'),
            },
          }
      }
    },

    showDisplayName: {
      get() {
        return this.displayNameState || Boolean(this.data.displayName)
      },
      set(val) {
        if (val) this.displayNameState = true
        else {
          this.displayNameState = false
          this.$nextTick(() => {
            this.data.displayName = null
          })
        }
      },
    },

    preventSubmit() {
      return !this.validator.passes || !this.hasChanges
    },
  },
  methods: {
    ...mapActions('Residential', {
      fetch(dispatch, ...rest) {
        return dispatch('fetch', rest).then(data => {
          if (!data) return this.$router.push({ name: 'notfound' })
          this.draft = data.project.draftId
        })
      },
      mutate: 'mutate',
      remove: 'remove',
    }),

    submit() {
      if (this.preventSubmit) return Promise.resolve()

      tempChanges = {
        initialState: this.data.sale.type,
      }
      return this.mutate([
        { id: this.id, ...this.getData({ diff: true }) },
        {
          draft: this.draft,
          strategy: (item, payload) => {
            const {
              url,
              previewUrl,
              project: { draftId },
            } = payload

            return mergeDeepRight(item, mergeDeepRight(this.data, { url, previewUrl, project: { draftId } }))
          },
        },
      ]).then(res => {
        // set draft id after autosave
        if (this.draft === true && this.$path('project.draftId', res)) this.draft = this.$path('project.draftId', res)

        return res
      })
    },

    complete() {
      if (!this.draft) this.draft = this.showDraft
      this.submit().then(() => this.$router.push({ name: 'project-dashboard', params: { id: this.item.project.id } }))
    },
    onNavigation(callback) {
      callback()
      if (this.preventSubmit) return
      // set draft so autosaves to draft
      if (!this.draft) this.draft = this.showDraft
      this.submit()
    },
    getStatusSuffix(status) {
      return getStatusSuffix(status, this.addons)
    },
    statusUpdate(val, oldVal) {
      if (!this.ready) return

      if (oldVal === 'reserved' && soldStates.includes(val)) {
        this.data.owners = this.data.reservedBy
      }

      if (['project:bid', 'project:presentation'].every(addon => this.addons.includes(addon)) && val === 'sold') {
        this.data.status = oldVal
        return (this.showSoldDialog = true)
      }

      if (this.addons.includes('project:shop-purchase') && ['draft', 'sold'].includes(val))
        return (this.data.status = val)

      if (!this.addons.includes('project:presentation') && !['draft', 'upcoming'].includes(val))
        return Bus.emit('pricing:show', {
          addon: 'project:presentation',
          options: { units: this.units },
          action: () => this.addon('project:presentation'),
        })
          .then(() => (this.data.status = val))
          .catch(() => (this.data.status = oldVal))
    },
    onSaleTypeChange(val, oldVal) {
      if (!this.ready) return

      if (oldVal === tempChanges.initialState) {
        tempChanges = {
          ...tempChanges,
          status: this.data.status,
          sale: {
            value: this.data.sale.value,
            jointDebt: this.data.sale.jointDebt,
          },
        }
      }

      let statuses = val === 'sale' ? ['rent', 'rented'] : ['sale', 'sold']
      if (statuses.includes(this.data.status)) this.data.status = 'draft'
      this.data.sale.value = null
      this.data.sale.jointDebt = null

      if (val === tempChanges.initialState) {
        PartialUpdate(this.data, tempChanges)
      }
    },
    addon(addon) {
      return this.$store
        .dispatch('Project/addon', {
          addon,
          state: true,
          project: this.item.project.id,
        })
        .then(() => this.addons.push(addon))
    },
  },

  metaInfo() {
    return {
      title: this.item.name,
    }
  },

  components: {
    StepperComponent,
    HeaderComponent,
    DropdownComponent,
    ModalComponent,
    Card,
    Actions,
    Badge,
    HelperPopover,
    FirstUnitCreatedAlert,
    DraftAlert,
    Preview,
    Draft,
  },
}
</script>

<style lang="scss">
.residential-mutate {
  background-color: $background-grey;

  &__template-info {
    display: flex;
    justify-content: end;
    width: fit-content;
    margin-left: auto;
    position: relative;
    transform: translate(-0px, -20px);
    @include respond-below('desktop') {
      padding-top: 1rem;
      transform: none;
    }

    .popover-component .trigger {
      display: initial !important;
      border: none;
      margin-right: 10px;
    }
    .badge {
      font-size: 0.9em;
      border-radius: var(--border-radius);
      background-color: var(--secondary);
      padding: 0.5rem;
    }
  }
  .elder-stepper__component > * {
    margin: 2rem auto;
  }
}
</style>
